import { differenceInDays, isValid, isWithinInterval } from "date-fns/esm";

import { AssignStatus, type IContactPackage } from "~/api/myedvenn/contact_package";
import type { IPackage } from "~/api/myedvenn/packages";
import type { IUser } from "~/api/myedvenn/user";
import type { IPackageFile } from "~/api/myedvenn/package_file";

import type { Api } from '~/plugins/02.api'

// timeout in minutes

const CONTACT_PACKAGE_TIMEOUT = 10
const PACKAGE_FILE_TIMEOUT = 10
const PACKAGE_TIMEOUT = 10
const USER_TIMEOUT = 30

type IContactPack = IContactPackage & {
    _state: {
        rawWorkDuration: number;
        completion: number;
        theoCompletion: number;
        assignsSubmitted: number;
    }
}

const cache: Map<string, Record<string, any>> = new Map()
const cacheTimeout: Map<string, Record<string, number>> = new Map()

export const useStoreCache = defineStore('cache', () => {

    const { $api } = useNuxtApp() as { $api: Api }

    const packageFiles = ref<Record<number, IPackageFile>>({})
    const packages = ref<Record<number, IPackage>>({})
    const contactPackages = ref<Record<number, IContactPack>>({})
    const users = ref<Record<number, IUser>>({})

    const allState = [packageFiles, packages, contactPackages, users]

    const loadPackageFile = getCache<number, IPackageFile>(
        'packageFileCache',
        {
            get: _pfId => packageFiles.value[_pfId],
            set: (_pfId, _pf) => { packageFiles.value[_pfId] = _pf }
        },
        _pfId => $api.packageFile
            .get({
                id: _pfId,
                relations: ['package', 'contactPackage', 'contact'],
                select: ['package.id', 'contactPackage.id', 'contact.id']
            })
        ,
        PACKAGE_FILE_TIMEOUT
    )

    const loadContactPackage = getCache<number, IContactPack>(
        'contactPackageCache',
        {
            get: _cpId => contactPackages.value[_cpId],
            set: (_cpId, _cp) => { contactPackages.value[_cpId] = _cp }
        },
        _cpId => $api.contactPackage
            .get({
                id: _cpId,
                relations: [
                    'package',

                    'timespans',
                    'trainings.modules.timespans',
                    'trainings.modules.sections.timespans',

                    'trainings.packageTraining',
                    'trainings.modules.packageTrainingModule',
                    'trainings.modules.sections.packageTrainingSection',
                    'trainings.modules.sections.assigns.packageTrainingAssign',
                ],
                select: [
                    'package.id',

                    'timespans.*',
                    'trainings.modules.timespans.*',

                    'trainings.modules.*',
                    'trainings.packageTraining.*',
                    'trainings.modules.packageTrainingModule.*',
                    'trainings.modules.sections.packageTrainingSection.*',

                    // progress
                    'trainings.modules.sections.completionPercentage',
                    'trainings.modules.sections.timespans.rawWorkDuration',

                    // assign
                    'trainings.modules.sections.assigns.*',
                    'trainings.modules.sections.assigns.packageTrainingAssign.*'
                ]
            })
            .then<IContactPack>(_cp => {
                const cp = _cp as IContactPack
                let workDuration = 0
                let workDurationDone = 0
                _cp.trainings.map(t => t.modules?.map(m => m.sections)).flat(2).filter(s => !!s).reverse()
                    .forEach(s => {
                        const sectionWorkDuration = s.timespans?.length ? s.timespans[0].rawWorkDuration : 0
                        workDuration += sectionWorkDuration
                        if (workDurationDone) workDurationDone += sectionWorkDuration
                        else if (s.completionPercentage) workDurationDone += (sectionWorkDuration * (s.completionPercentage / 100))
                    })
                const completion = workDuration ? (workDurationDone / workDuration) * 100 : 0

                let theoCompletion = 0
                const now = new Date()
                const currentPeriodIndex = _cp.timespans
                    ?.findIndex(it =>
                        isValid(it.startAt) && isValid(it.endAt) && isWithinInterval(now, { start: it.startAt, end: it.endAt })
                    )
                if (!_cp.timespans?.length || _cp.timespans[_cp.timespans.length - 1].endAt < now.getTime())
                    theoCompletion = 100
                else if (currentPeriodIndex !== -1) {
                    const currentPeriodEnd = _cp.timespans[currentPeriodIndex].endAt
                    const full = differenceInDays(_cp.timespans[0].endAt, _cp.timespans[0].startAt)
                    const remaining = differenceInDays(currentPeriodEnd, now)
                    theoCompletion = Math.min((1 - (remaining / full)) * 100, 100)
                }

                cp._state = {
                    rawWorkDuration: (_cp.trainings || [])
                        .reduce((tt, t) => tt + (t.modules || [])
                            .reduce((mt, m) => mt + (m.timespans?.length ? m.timespans[0].rawWorkDuration : 0), 0), 0),

                    assignsSubmitted: _cp.trainings
                        .map(t => (t.modules || []).map(m => m.sections.map(s => s.assigns))).flat(3)
                        .filter(a => a?.status === AssignStatus.SUBMITTED)
                        .length,
                    completion,
                    theoCompletion
                }

                return cp
            })
        ,
        CONTACT_PACKAGE_TIMEOUT
    )

    const loadPackage = getCache<number, IPackage>(
        'packageCache',
        {
            get: _packId => packages.value[_packId],
            set: (_packId, _pack) => { packages.value[_packId] = _pack }
        },
        // use find() because get() with a 404 error triggers a toast display
        packId => $api.pack.find({ criterias: [{ field: 'id', value: `${packId}` }], limit: 1 }),
        PACKAGE_TIMEOUT
    )

    const loadUser = getCache<number, IPackage>(
        'userCache',
        {
            get: _userId => users.value[_userId],
            set: (_userId, _user) => { users.value[_userId] = _user }
        },
        // use find() because get() with a 404 error triggers a toast display
        _userId => $api.user.find({ criterias: [{ field: 'id', value: `${_userId}` }], limit: 1 }),
        USER_TIMEOUT
    )

    const getPackageFile = computed(() => lazyLoad(_pfId => {
        loadPackageFile(_pfId, false);
        return packageFiles.value[_pfId]
    }))

    const getContactPackage = computed(() => lazyLoad((_cpId: number) => {
        loadContactPackage(_cpId, false);
        return contactPackages.value[_cpId]
    }))

    const getPackage = computed(() => lazyLoad(_pId => {
        loadPackage(_pId, false);
        return packages.value[_pId]
    }))

    const getUser = computed(() => lazyLoad(_uId => {
        loadUser(_uId, false);
        return users.value[_uId]
    }))

    return {
        // state
        packages,
        packageFiles,
        contactPackages,
        users,

        // getters
        getContactPackage,
        getPackageFile,
        getPackage,
        getUser,

        // actions

        /** async get cache and/or fetch */
        loadContactPackage,
        /** async get cache and/or fetch */
        loadPackage,
        /** async get cache and/or fetch */
        loadUser,
        /** async get cache and/or fetch */
        loadPackageFile,

        reset: () => {
            allState.forEach(r => r.value = {})
            cache.clear()
            cacheTimeout.clear()
        }
    }
})

type LazyResource<T> = T | { id?: number; } | number

function lazyLoad<T extends { id?: number; }>(call: (id: number) => T) {
    return (a: LazyResource<T>) => {
        if (typeof a === "number")
            return call(a)

        if (a?.id && Object.keys(a).some(k => k === 'id' || a[k] === undefined))
            return call(a.id)

        return a as T
    }
}

function getCache<Arg, Resource>(
    cacheId: string,
    store: {
        get(a: Arg): Resource;
        set(a: Arg, r: Resource)
    },
    callApi: ((a: Arg) => Promise<Resource>) | ((a: Arg) => Promise<{ values: Resource[]; count?: number }>),
    /** in minutes */
    timeout?: number
) {
    return async (a: Arg, force = true) => {
        const _store = store.get(a)
        if (timeout === undefined && _store) return _store

        if (!cache.has(cacheId)) cache.set(cacheId, {})
        const cacheResources = cache.get(cacheId)

        if (!cacheTimeout.has(cacheId)) cacheTimeout.set(cacheId, {})
        const cacheTimeoutResources = cacheTimeout.get(cacheId)

        const cacheResourceId = JSON.stringify(a)
        let _cacheResource = cacheResources[cacheResourceId] as Promise<Resource>
        let _cacheTimeout = cacheTimeoutResources[cacheResourceId]

        if (force || !_cacheResource || timeout === undefined || !_cacheTimeout || (new Date().getTime() - _cacheTimeout > (timeout * 60 * 1000 ))) {
            cacheTimeoutResources[cacheResourceId] = new Date().getTime()
            _cacheResource = callApi(a)
                .then(async c => {
                    let res = null
                    if ('count' in c && 'values' in c && Array.isArray(c.values)) {
                        if (c.count === 1 || (!c.count && c.values.length === 1))
                            res = c.values[0]
                        else
                            console.warn(`cache [${cacheId}] Not Found ${a}. Wrong count ${c.count}`)
                    } else if (!c) {
                        console.warn(`cache [${cacheId}] Not Found ${a}`)
                    } else {
                        res = c
                    }
                    store.set(a, res)
                    return res
                }).catch(e => {
                    console.warn(`cache [${cacheId}] Not Found ${a}`, e)
                    store.set(a, null)
                    return null
                })
            cacheResources[cacheResourceId] = _cacheResource
        }
        if (!_store)
            await _cacheResource
        return store.get(a)
    }
}